import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { t, translateError } from '../i18n.js';
import { api, authState } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import { parseServerDate } from '../utils/dates.js';
import ItemGoals from '../components/shared/GoalChips.jsx';
import { memberLabel } from '../utils/away.js';
import { openFile } from '../utils/openFile.js';
import { DepartmentLabelById } from '../components/shared/DepartmentLabel.jsx';

const STATUS_TRANSITIONS = {
  nieuw: [{ label: 'process.actions.activate', next: 'actief' }],
  actief: [
    { label: 'process.actions.waitForInput', next: 'wacht_op_input' },
    { label: 'process.actions.complete', next: 'afgerond' },
    { label: 'process.actions.cancel', next: 'geannuleerd', variant: 'danger' },
  ],
  wacht_op_input: [
    { label: 'process.actions.activate', next: 'actief' },
    { label: 'process.actions.complete', next: 'afgerond' },
    { label: 'process.actions.cancel', next: 'geannuleerd', variant: 'danger' },
  ],
};

function SkeletonLine({ width = '100%', height = '16px', style = '' }) {
  return <div class="skeleton" style={`width:${width};height:${height};${style}`} />;
}

function ProcessDetailSkeleton() {
  return (
    <div>
      <div class="page-header">
        <SkeletonLine width="80px" height="32px" />
      </div>
      <div class="card mt-4" style="padding:20px;margin-bottom:16px">
        <SkeletonLine width="50%" height="24px" style="margin-bottom:16px" />
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">
          {[0,1,2,3].map(i => <SkeletonLine key={i} height="40px" />)}
        </div>
        <SkeletonLine width="160px" height="32px" />
      </div>
      <div style="display:flex;gap:16px;align-items:start">
        <div style="flex:1">
          {[0,1,2].map(i => (
            <div key={i} class="card" style="padding:20px;margin-bottom:12px">
              <SkeletonLine width="40%" height="16px" style="margin-bottom:12px" />
              <SkeletonLine height="12px" style="margin-bottom:8px" />
              <SkeletonLine width="80%" height="12px" style="margin-bottom:16px" />
              <SkeletonLine width="120px" height="32px" />
            </div>
          ))}
        </div>
        <div style="width:280px">
          <SkeletonLine height="120px" />
        </div>
      </div>
    </div>
  );
}

// Why local: started_at is a UTC server timestamp; the date field and the shown date
// must agree for a process started just after midnight.
function localDateInput(value) {
  const d = parseServerDate(value);
  if (isNaN(d)) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function ProcessDetailView({ id }) {
  const auth = useAuth();
  const [process, setProcess] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState(null);
  const [completingStep, setCompletingStep] = useState(null);
  const [conditionAnswers, setConditionAnswers] = useState({}); // stepId → 'ja'|'nee'
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Inline edit state for assigned_to, deadline and started_at
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [editStartedAt, setEditStartedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const successTimer = useRef(null);

  // Ownership: assigned_to is the current owner, falling back to started_by for unassigned processes.
  // Club admin (settings.update) and platform admin can always edit/delete.
  const currentUserId = Number(authState.value.userId);
  const isClubAdmin = auth.isPlatformAdmin || auth.hasPermission('settings', 'update');
  const isProcessOwner = !!process && (
    Number(process.assigned_to) === currentUserId ||
    (!process.assigned_to && Number(process.started_by) === currentUserId)
  );
  // Standing in for the owner during a vakantiedelegatie: may finish steps, not edit the process
  const actingFor = process?.acting_for || null;
  const canUpdate = isProcessOwner || isClubAdmin;
  const canFinishSteps = canUpdate || !!actingFor;
  const canDelete = isProcessOwner || isClubAdmin;

  const fetchProcess = () => {
    setLoading(true);
    Promise.all([
      api(`/api/processes/${id}`),
      api('/api/users/members').catch(() => []),
    ])
      .then(([proc, usrs]) => {
        setProcess(proc);
        setUsers(Array.isArray(usrs) ? usrs : []);
        setEditAssignedTo(proc.assigned_to ? String(proc.assigned_to) : '');
        setEditDeadline(proc.deadline ? proc.deadline.slice(0, 10) : '');
        setEditStartedAt(proc.started_at ? localDateInput(proc.started_at) : '');
      })
      .catch((err) => setError(translateError(err.code, err.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProcess(); }, [id]);

  const handleTransition = async (nextStatus) => {
    setTransitioning(true);
    setTransitionError(null);
    try {
      const updated = await api(`/api/processes/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      setProcess((prev) => ({ ...prev, ...updated }));
      fetchProcess();
    } catch (err) {
      setTransitionError(translateError(err.code, err.detail));
    } finally {
      setTransitioning(false);
    }
  };

  const handleCompleteStep = async (stepId, conditionAnswer = null) => {
    setCompletingStep(stepId);
    try {
      await api(`/api/processes/${id}/steps/${stepId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'completed', condition_answer: conditionAnswer }),
      });
      fetchProcess();
    } catch {
      fetchProcess();
    } finally {
      setCompletingStep(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    clearTimeout(successTimer.current);
    try {
      const updated = await api(`/api/processes/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          assigned_to: editAssignedTo ? parseInt(editAssignedTo) : null,
          deadline: editDeadline || null,
          started_at: editStartedAt || null,
        }),
      });
      setProcess(updated);
      setSaveSuccess(true);
      successTimer.current = setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setTransitionError(translateError(err.code, err.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api(`/api/processes/${id}`, { method: 'DELETE' });
      route('/processes');
    } catch (err) {
      setTransitionError(translateError(err.code, err.detail));
      setDeleteModalOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (val) => val ? parseServerDate(val).toLocaleDateString() : '-';

  const formatSlaCountdown = (slaDeadlineAt) => {
    if (!slaDeadlineAt) return null;
    const diff = new Date(slaDeadlineAt) - new Date();
    const hours = Math.round(diff / 3600000);
    if (diff < 0) return { label: `${Math.abs(hours)}u over tijd`, color: '#dc2626', bg: '#fee2e2' };
    if (hours < 24) return { label: `${hours}u resterend`, color: '#d97706', bg: '#fef3c7' };
    const days = Math.floor(hours / 24);
    return { label: `${days}d resterend`, color: '#6b7280', bg: '#f3f4f6' };
  };

  if (loading) return <ProcessDetailSkeleton />;

  if (error) {
    return (
      <div>
        <Button variant="secondary" onClick={() => route('/processes')}>{t('common.back')}</Button>
        <div class="alert alert-error mt-4">{error}</div>
      </div>
    );
  }

  if (!process) return null;

  // Changing the process status is for its owner or an admin (the server checks the same)
  const transitions = canUpdate ? (STATUS_TRANSITIONS[process.status] || []) : [];
  const isFinished = process.status === 'afgerond' || process.status === 'geannuleerd';

  // Group tasks by process_step_id for quick lookup
  const tasksByStep = {};
  (process.tasks || []).forEach((task) => {
    const key = task.process_step_id ?? 'unlinked';
    if (!tasksByStep[key]) tasksByStep[key] = [];
    tasksByStep[key].push(task);
  });
  const unlinkedTasks = tasksByStep['unlinked'] || [];

  return (
    <div>
      <div class="page-header">
        <Button variant="secondary" size="sm" onClick={() => route(auth.hasPermission('processes', 'read') ? '/processes' : '/dashboard')}>
          {t('common.back')}
        </Button>
        {canDelete && (
          <Button variant="danger" size="sm" onClick={() => setDeleteModalOpen(true)}>
            {t('process.delete')}
          </Button>
        )}
      </div>

      {actingFor && !canUpdate && (
        <div class="alert mb-4" style="background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);font-size:14px">
          🌴 Je vervangt {actingFor.name}. Je kunt de stappen van dit proces afronden zolang de vervanging loopt.
        </div>
      )}

      {/* Process header card */}
      <Card style="margin-bottom:16px">
        <div class="flex-between mb-3">
          <h2 style="font-size:20px;font-weight:700;margin:0">{process.title}</h2>
          <div style="display:flex;align-items:center;gap:8px">
            <DepartmentLabelById id={process.department_id} />
            <StatusBadge status={process.status} />
          </div>
        </div>
        <div style="margin:-8px 0 12px"><ItemGoals type="process" id={process.id} /></div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">
          <div class="detail-item">
            <span class="detail-label">{t('process.startedBy')}</span>
            <span class="detail-value">{process.started_by_name || '-'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Start</span>
            {canUpdate && !isFinished ? (
              <input
                class="form-input"
                type="date"
                style="margin-top:4px;padding:4px 8px;height:32px;font-size:13px"
                value={editStartedAt}
                onInput={(e) => setEditStartedAt(e.target.value)}
              />
            ) : (
              <span class="detail-value">{formatDate(process.started_at)}</span>
            )}
          </div>

          {/* Editable: assigned to */}
          <div class="detail-item">
            <span class="detail-label">{t('process.assignedTo')}</span>
            {canUpdate && !isFinished ? (
              <select
                class="form-select"
                style="margin-top:4px;padding:4px 8px;height:32px;font-size:13px"
                value={editAssignedTo}
                onChange={(e) => setEditAssignedTo(e.target.value)}
              >
                <option value="">—</option>
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>{memberLabel(u)}</option>
                ))}
              </select>
            ) : (
              <span class="detail-value">{process.assigned_to_name || '-'}</span>
            )}
          </div>

          {/* Editable: deadline */}
          <div class="detail-item">
            <span class="detail-label">{t('process.deadline')}</span>
            {canUpdate && !isFinished ? (
              <input
                class="form-input"
                type="date"
                style="margin-top:4px;padding:4px 8px;height:32px;font-size:13px"
                value={editDeadline}
                onInput={(e) => setEditDeadline(e.target.value)}
              />
            ) : (
              <span class="detail-value">{formatDate(process.deadline)}</span>
            )}
          </div>

          {process.status === 'afgerond' && process.completed_at && (
            <div class="detail-item">
              <span class="detail-label">{t('process.completedAt')}</span>
              <span class="detail-value">{formatDate(process.completed_at)}</span>
            </div>
          )}
        </div>

        {transitionError && <div class="alert alert-error mb-3">{transitionError}</div>}
        {saveSuccess && <div class="alert alert-success mb-3">{t('club.saved')}</div>}

        <div class="flex gap-2 flex-wrap">
          {canUpdate && !isFinished && (
            <Button variant="secondary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          )}
          {transitions.map((tr) => (
            <Button
              key={tr.next}
              variant={tr.variant || 'primary'}
              size="sm"
              disabled={transitioning}
              onClick={() => handleTransition(tr.next)}
            >
              {t(tr.label)}
            </Button>
          ))}
        </div>
      </Card>

      {/* Progress bar */}
      {process.steps?.length > 0 && (() => {
        const relevant = process.steps.filter(s => s.status !== 'skipped');
        const total = relevant.length;
        const done = relevant.filter(s => s.status === 'completed').length;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <div class="card" style="margin-bottom:16px;padding:16px 20px">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
              <span style="font-size:13px;font-weight:600;color:var(--color-text)">Voortgang</span>
              <span style="font-size:13px;color:var(--color-text-muted)">
                {done} van {total} stappen voltooid
              </span>
            </div>
            <div style="height:8px;background:var(--color-border);border-radius:99px;overflow:hidden">
              <div style={`height:100%;width:${pct}%;background:${pct === 100 ? 'var(--color-success,#16a34a)' : 'var(--color-primary)'};border-radius:99px;transition:width 0.4s ease`} />
            </div>
            {pct === 100 && (
              <p style="margin:8px 0 0;font-size:12px;color:var(--color-success,#16a34a);font-weight:500">✓ Alle stappen afgerond</p>
            )}
          </div>
        );
      })()}

      {/* Main layout: timeline (left) + documents (right) */}
      <div style="display:flex;gap:16px;align-items:start">

        {/* Timeline steps */}
        <div style="flex:1;min-width:0">
          {process.steps?.length > 0 && (
            <div style="position:relative">
              {/* Vertical connector line */}
              <div style="position:absolute;left:19px;top:20px;bottom:20px;width:2px;background:var(--color-border);z-index:0" />

              {process.steps.map((step, idx) => {
                const isCompleted = step.status === 'completed';
                const isActive = step.status === 'active';
                const isSkipped = step.status === 'skipped';
                const isPending = !isCompleted && !isActive && !isSkipped;
                const isCondition = step.step_type === 'condition';
                const isParallel = !!step.parallel_group;
                const isLast = idx === process.steps.length - 1;
                const stepTasks = tasksByStep[step.id] || [];
                const slaInfo = isActive ? formatSlaCountdown(step.sla_deadline_at) : null;

                const nodeColor = isCompleted
                  ? 'var(--color-success,#16a34a)'
                  : isActive
                  ? 'var(--color-primary)'
                  : isSkipped
                  ? '#d1d5db'
                  : 'var(--color-border,#e5e7eb)';
                const nodeTextColor = (isPending || isSkipped) ? 'var(--color-text-muted)' : '#fff';

                const nodeIcon = isSkipped ? '–'
                  : isCompleted ? (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  )
                  : isCondition ? '◇'
                  : isParallel ? '⇉'
                  : step.step_order;

                return (
                  <div key={step.id} style={`display:flex;gap:16px;align-items:flex-start;${!isLast ? 'margin-bottom:8px' : ''}`}>

                    {/* Node */}
                    <div style="flex-shrink:0;display:flex;flex-direction:column;align-items:center;z-index:1;padding-top:16px">
                      <div style={`
                        width:38px;height:38px;border-radius:${isCondition ? '6px' : isParallel ? '8px' : '50%'};
                        background:${nodeColor};
                        color:${nodeTextColor};
                        display:flex;align-items:center;justify-content:center;
                        font-size:${isCondition || isParallel ? '16px' : '14px'};font-weight:700;
                        border:3px solid ${isActive ? 'var(--color-primary)' : isCompleted ? 'var(--color-success,#16a34a)' : isSkipped ? '#d1d5db' : 'var(--color-border)'};
                        box-shadow:${isActive ? '0 0 0 4px color-mix(in srgb, var(--color-primary) 15%, transparent)' : 'none'};
                        transition:all 0.2s;opacity:${isSkipped ? 0.5 : 1};
                      `}>
                        {nodeIcon}
                      </div>
                    </div>

                    {/* Step card */}
                    <div style={`
                      flex:1;min-width:0;
                      background:${isSkipped ? 'var(--color-bg-secondary)' : 'var(--color-surface)'};
                      border:1px solid ${isActive ? 'var(--color-primary)' : isSkipped ? 'var(--color-border)' : 'var(--color-border)'};
                      border-radius:12px;
                      padding:16px;
                      margin-bottom:${!isLast ? '8px' : '0'};
                      opacity:${isSkipped ? 0.55 : isPending ? 0.75 : 1};
                      transition:border-color 0.2s,opacity 0.2s;
                    `}>
                      {/* Step header */}
                      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:8px;flex-wrap:wrap">
                        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                          <span style="font-weight:700;font-size:15px">{step.title}</span>
                          {step.is_approval_step && (
                            <span style="font-size:11px;background:#dbeafe;color:#1d4ed8;padding:2px 8px;border-radius:10px;font-weight:500">
                              {t('template.isApprovalStep')}
                            </span>
                          )}
                          {isActive && (
                            <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:2px 8px;border-radius:10px;font-weight:500">
                              Actief
                            </span>
                          )}
                          {isCondition && (
                            <span style="font-size:11px;background:#fef3c7;color:#92400e;padding:2px 8px;border-radius:10px;font-weight:500">◇ Conditie</span>
                          )}
                          {isParallel && (
                            <span style="font-size:11px;background:#ede9fe;color:#6d28d9;padding:2px 8px;border-radius:10px;font-weight:500">⇉ {step.parallel_group}</span>
                          )}
                          {isSkipped && (
                            <span style="font-size:11px;background:#f3f4f6;color:#9ca3af;padding:2px 8px;border-radius:10px;font-weight:500">Overgeslagen</span>
                          )}
                          {/* SLA badge */}
                          {slaInfo && (
                            <span style={`font-size:11px;padding:2px 8px;border-radius:10px;font-weight:600;background:${slaInfo.bg};color:${slaInfo.color}`}>
                              ⏱ {slaInfo.label}
                            </span>
                          )}
                        </div>
                        <div style="display:flex;align-items:center;gap:8px;flex-shrink:0;flex-wrap:wrap">
                          {step.responsible_role_name && (
                            <span style="font-size:11px;background:var(--color-bg-secondary,#f9fafb);border:1px solid var(--color-border);color:var(--color-text-muted);padding:2px 8px;border-radius:10px">
                              {step.responsible_role_name}
                            </span>
                          )}
                          {isCompleted && step.completed_by_name && (
                            <span style="font-size:11px;color:var(--color-text-muted)">
                              ✓ {step.completed_by_name} · {formatDate(step.completed_at)}
                            </span>
                          )}
                        </div>
                      </div>

                      {step.description && (
                        <p style="font-size:13px;color:var(--color-text-muted);margin:0 0 10px;line-height:1.6">{step.description}</p>
                      )}

                      {/* Completed condition: show answer */}
                      {isCompleted && isCondition && step.condition_answer && (
                        <div style="margin-bottom:10px">
                          <span style={`font-size:12px;font-weight:600;padding:3px 10px;border-radius:10px;${step.condition_answer === 'ja' ? 'background:#dcfce7;color:#16a34a' : 'background:#fee2e2;color:#dc2626'}`}>
                            {step.condition_answer === 'ja' ? '✓ Ja' : '✗ Nee'}
                          </span>
                        </div>
                      )}

                      {/* Tasks */}
                      {stepTasks.length > 0 && (
                        <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden;margin-bottom:10px">
                          {stepTasks.map((task, tidx) => (
                            <div
                              key={task.id}
                              class="list-item"
                              style={`cursor:pointer;padding:8px 12px;${tidx > 0 ? 'border-top:1px solid var(--color-border)' : ''}`}
                              onClick={() => route(`/tasks/${task.id}`)}
                            >
                              <div style="flex:1;min-width:0">
                                <div style="font-size:13px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{task.title}</div>
                                {task.owner_name && <div style="font-size:11px;color:var(--color-text-muted)">{task.owner_name}</div>}
                              </div>
                              <StatusBadge status={task.status} type="task" />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Condition step — Ja/Nee buttons when active */}
                      {isActive && isCondition && !isFinished && canFinishSteps && (
                        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:12px;margin-bottom:10px">
                          <div style="font-size:13px;font-weight:600;margin-bottom:8px;color:#92400e">
                            ◇ {step.condition_question || 'Wat is de uitkomst?'}
                          </div>
                          <div style="display:flex;gap:8px">
                            <button
                              style={`padding:8px 20px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;border:2px solid;transition:all .15s;${conditionAnswers[step.id] === 'ja' ? 'background:#16a34a;color:#fff;border-color:#16a34a' : 'background:transparent;color:#16a34a;border-color:#16a34a'}`}
                              onClick={() => setConditionAnswers(p => ({ ...p, [step.id]: 'ja' }))}
                              disabled={completingStep === step.id}
                            >✓ Ja</button>
                            <button
                              style={`padding:8px 20px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;border:2px solid;transition:all .15s;${conditionAnswers[step.id] === 'nee' ? 'background:#dc2626;color:#fff;border-color:#dc2626' : 'background:transparent;color:#dc2626;border-color:#dc2626'}`}
                              onClick={() => setConditionAnswers(p => ({ ...p, [step.id]: 'nee' }))}
                              disabled={completingStep === step.id}
                            >✗ Nee</button>
                            {conditionAnswers[step.id] && (
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={completingStep === step.id}
                                onClick={() => handleCompleteStep(step.id, conditionAnswers[step.id])}
                              >
                                {completingStep === step.id ? t('common.loading') : 'Bevestigen →'}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Normal complete button (non-condition) */}
                      {!isCompleted && !isSkipped && !isFinished && canFinishSteps && isActive && !isCondition && (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={completingStep === step.id}
                          onClick={() => handleCompleteStep(step.id)}
                        >
                          {completingStep === step.id ? t('common.loading') : t('process.completeStep')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tasks not linked to a step */}
          {unlinkedTasks.length > 0 && (
            <Card title={t('process.tasks')} style="margin-top:12px">
              {unlinkedTasks.map((task) => (
                <div key={task.id} class="list-item" style="cursor:pointer" onClick={() => route(`/tasks/${task.id}`)}>
                  <div style="flex:1;font-size:14px;font-weight:500">{task.title}</div>
                  <StatusBadge status={task.status} type="task" />
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* Documents sidebar */}
        <div style="width:280px;flex-shrink:0">
          <Card title={t('process.documents')}>
            {!process.documents?.length ? (
              <p class="text-muted text-sm">{t('common.noResults')}</p>
            ) : (
              process.documents.map((doc) => (
                <div key={doc.id} class="list-item" style="flex-direction:column;align-items:flex-start;gap:4px">
                  <div style="font-size:13px;font-weight:500;word-break:break-word">{doc.file_name}</div>
                  <Button variant="secondary" size="sm" onClick={() => openFile(`/api/documents/${doc.id}/download`, doc.file_name)}>
                    {t('document.download')}
                  </Button>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>

      <Modal
        isOpen={deleteModalOpen}
        title={t('process.delete')}
        onClose={() => !deleting && setDeleteModalOpen(false)}
      >
        <p style="margin-bottom:24px;color:var(--color-text-muted);line-height:1.6">
          {t('process.confirmDelete')}
        </p>
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
